import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
} from "@mui/material";
import { tokens } from "@/tokens";
import { useUpdateFirstSaleSupplierMutation } from "@/services/first-sale";

interface EditSupplierModalProps {
  open: boolean;
  onClose: () => void;
  supplier: any;
  tenantId?: string;
}

export const EditSupplierModal: React.FC<EditSupplierModalProps> = ({
  open,
  onClose,
  supplier,
  tenantId,
}) => {
  const [form, setForm] = useState({
    supplierName: "",
    mid: "",
    country: "",
    factoryName: "",
    factoryCountry: "",
  });

  const [updateSupplier, { isLoading }] = useUpdateFirstSaleSupplierMutation();

  useEffect(() => {
    if (supplier && open) {
      setForm({
        supplierName: supplier.supplierName || "",
        mid: supplier.mid || "",
        country: supplier.country || "",
        factoryName: supplier.factoryName || "",
        factoryCountry: supplier.factoryCountry || "",
      });
    }
  }, [supplier, open]);

  const handleSubmit = async () => {
    if (!form.supplierName.trim() || !form.mid.trim()) return;

    try {
      await updateSupplier({
        supplierId: supplier._id,
        ...(tenantId && { tenantId }),
        input: {
          supplierName: form.supplierName.trim(),
          mid: form.mid.trim(),
          country: form.country.trim() || undefined,
          factoryName: form.factoryName.trim() || undefined,
          factoryCountry: form.factoryCountry.trim() || undefined,
        },
      }).unwrap();

      onClose();
    } catch (err) {
      console.error("Failed to update supplier:", err);
    }
  };

  const canSave = form.supplierName.trim() && form.mid.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: tokens.radius.lg,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: tokens.fontSize.lg,
          fontWeight: tokens.fontWeight.heavy,
          color: tokens.text.primary,
          pb: 0,
        }}
      >
        Edit Supplier
      </DialogTitle>
      <DialogContent sx={{ pt: "12px !important" }}>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Supplier Name"
            size="small"
            fullWidth
            required
            value={form.supplierName}
            onChange={(e) => setForm((prev) => ({ ...prev, supplierName: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
          />
          <TextField
            label="MID (Manufacturer ID)"
            size="small"
            fullWidth
            required
            value={form.mid}
            onChange={(e) => setForm((prev) => ({ ...prev, mid: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
          />
          <TextField
            label="Country"
            size="small"
            fullWidth
            value={form.country}
            onChange={(e) => setForm((prev) => ({ ...prev, country: e.target.value }))}
            placeholder="e.g., CN"
            sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
          />
          <TextField
            label="Factory Name"
            size="small"
            fullWidth
            value={form.factoryName}
            onChange={(e) => setForm((prev) => ({ ...prev, factoryName: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
          />
          <TextField
            label="Factory Country"
            size="small"
            fullWidth
            value={form.factoryCountry}
            onChange={(e) => setForm((prev) => ({ ...prev, factoryCountry: e.target.value }))}
            placeholder="e.g., CN"
            sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          sx={{
            textTransform: "none",
            fontSize: tokens.fontSize.sm2,
            color: tokens.text.secondary,
            "&:hover": { bgcolor: tokens.bg.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSave || isLoading}
          sx={{
            textTransform: "none",
            fontSize: tokens.fontSize.sm2,
            fontWeight: tokens.fontWeight.semibold,
            bgcolor: tokens.brand.primary,
            borderRadius: tokens.radius.md,
            boxShadow: "none",
            px: 3,
            "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
          }}
        >
          {isLoading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSupplierModal;
